import { useEffect, useState } from "react";
import { Copy, RefreshCw, Send, Sparkles, MessageCircle, Mail, Bell } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, apiError } from "@/lib/api";
import { PageHeader, Panel, Field, ErrorText, Spinner, btnPrimary, btnSecondary, btnGhost, inputCls, fmtDateTime } from "@/components/portal/ui";

const WEBHOOK_URL = `${process.env.REACT_APP_BACKEND_URL}/api/whatsapp/webhook`;

const randomToken = () => Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, "0")).join("");

function SettingsForm({ settings, onSaved }) {
  const [form, setForm] = useState({ ...settings, access_token: "" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        display_phone: form.display_phone,
        phone_number_id: form.phone_number_id,
        verify_token: form.verify_token,
        active: form.active,
        auto_reply_enabled: form.auto_reply_enabled,
        notify_staff_email: form.notify_staff_email,
        notify_staff_whatsapp: form.notify_staff_whatsapp,
        notify_patient_whatsapp: form.notify_patient_whatsapp,
        welcome_message: form.welcome_message,
        access_token: form.access_token ? form.access_token : null,
      };
      const { data } = await api.put("/whatsapp/settings", payload);
      toast.success("Impostazioni WhatsApp salvate");
      onSaved(data);
      setForm({ ...data, access_token: "" });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copiato");
    } catch {
      toast.error("Copia non riuscita");
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-5" data-testid="whatsapp-settings-form">
      <div className="grid grid-cols-1 gap-4 rounded-2xl bg-[#F6F4FA] p-4 sm:grid-cols-2">
        <label className="flex items-start gap-3">
          <Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} className="mt-0.5 data-[state=checked]:bg-[#25D366]" data-testid="whatsapp-active-switch" />
          <span>
            <span className="block text-sm font-semibold">Canale WhatsApp attivo sul sito</span>
            <span className="block text-xs text-[#6B6280]">Se disattivo, i pulsanti del sito mostrano "Il servizio sarà presto disponibile nella tua zona".</span>
          </span>
        </label>
        <label className="flex items-start gap-3">
          <Switch checked={form.auto_reply_enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, auto_reply_enabled: v }))} className="mt-0.5 data-[state=checked]:bg-[#5B2C91]" data-testid="whatsapp-autoreply-switch" />
          <span>
            <span className="block text-sm font-semibold">Risposta automatica sulle disponibilità</span>
            <span className="block text-xs text-[#6B6280]">Chi scrive riceve gli orari liberi dei medici in base alla specialità richiesta.</span>
          </span>
        </label>
      </div>

      <div className="rounded-2xl border border-[#5B2C91]/12 p-4" data-testid="whatsapp-notify-section">
        <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#6B6280]">Notifiche automatiche visite</p>
        <p className="mt-1 text-xs text-[#8A809A]">Quando crei o modifichi una visita, avvisiamo subito il medico e l'infermiere assegnati. Se cambi orario o annulli, avvisiamo anche il paziente via WhatsApp.</p>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="flex items-start gap-3">
            <Switch checked={form.notify_staff_email} onCheckedChange={(v) => setForm((f) => ({ ...f, notify_staff_email: v }))} className="mt-0.5 data-[state=checked]:bg-[#5B2C91]" data-testid="notify-staff-email-switch" />
            <span className="block text-sm font-semibold">Email a medico e infermiere</span>
          </label>
          <label className="flex items-start gap-3">
            <Switch checked={form.notify_staff_whatsapp} onCheckedChange={(v) => setForm((f) => ({ ...f, notify_staff_whatsapp: v }))} className="mt-0.5 data-[state=checked]:bg-[#25D366]" data-testid="notify-staff-whatsapp-switch" />
            <span className="block text-sm font-semibold">WhatsApp a medico e infermiere</span>
          </label>
          <label className="flex items-start gap-3">
            <Switch checked={form.notify_patient_whatsapp} onCheckedChange={(v) => setForm((f) => ({ ...f, notify_patient_whatsapp: v }))} className="mt-0.5 data-[state=checked]:bg-[#25D366]" data-testid="notify-patient-whatsapp-switch" />
            <span className="block text-sm font-semibold">WhatsApp al paziente se cambia/annulla</span>
          </label>
        </div>
        <p className="mt-3 text-xs text-[#8A809A]">Le email partono subito. Le notifiche WhatsApp richiedono la Cloud API configurata e attiva qui sotto (e, per l'invio proattivo, i template approvati da Meta).</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Numero WhatsApp (mostrato sul sito)" htmlFor="wa-phone" hint="Formato internazionale, es. +39 335 833 0304">
          <input id="wa-phone" value={form.display_phone} onChange={set("display_phone")} className={inputCls} placeholder="+39 …" data-testid="whatsapp-phone-input" />
        </Field>
        <Field label="Phone Number ID (Meta)" htmlFor="wa-pnid" hint="Da Meta for Developers → WhatsApp → API Setup">
          <input id="wa-pnid" value={form.phone_number_id} onChange={set("phone_number_id")} className={inputCls} placeholder="1234567890" data-testid="whatsapp-pnid-input" />
        </Field>
        <Field label="Access Token (Meta)" htmlFor="wa-token" hint={settings.has_access_token ? `Token salvato: ${settings.access_token_masked}. Lascia vuoto per non modificarlo.` : "Token di sistema permanente (whatsapp_business_messaging)."}>
          <input id="wa-token" type="password" autoComplete="off" value={form.access_token} onChange={set("access_token")} className={inputCls} placeholder={settings.has_access_token ? "•••••••• (invariato)" : "EAAG…"} data-testid="whatsapp-token-input" />
        </Field>
        <Field label="Verify Token (webhook)" htmlFor="wa-verify" hint="Inserisci lo stesso valore nella configurazione webhook di Meta.">
          <div className="flex gap-2">
            <input id="wa-verify" value={form.verify_token} onChange={set("verify_token")} className={inputCls} data-testid="whatsapp-verify-input" />
            <button type="button" onClick={() => setForm((f) => ({ ...f, verify_token: randomToken() }))} className={btnSecondary} aria-label="Genera token" data-testid="whatsapp-verify-generate"><RefreshCw className="h-4 w-4" /></button>
          </div>
        </Field>
      </div>

      <Field label="Callback URL del webhook" hint="Copia questo URL in Meta → WhatsApp → Configuration → Webhook, e iscriviti al campo “messages”.">
        <div className="flex gap-2">
          <input readOnly value={WEBHOOK_URL} className={`${inputCls} font-mono text-xs`} data-testid="whatsapp-webhook-url" />
          <button type="button" onClick={() => copy(WEBHOOK_URL)} className={btnSecondary} aria-label="Copia URL" data-testid="whatsapp-webhook-copy"><Copy className="h-4 w-4" /></button>
        </div>
      </Field>

      <Field label="Messaggio di benvenuto" htmlFor="wa-welcome" hint="Prima riga della risposta quando non viene riconosciuta una specialità. Lascia vuoto per usare quello predefinito.">
        <textarea id="wa-welcome" rows={3} value={form.welcome_message} onChange={set("welcome_message")} className={inputCls} placeholder="Ciao! Sono l'assistente di TytoCare Italia…" data-testid="whatsapp-welcome-input" />
      </Field>

      <ErrorText testid="whatsapp-form-error">{error}</ErrorText>
      <div className="flex justify-end">
        <button type="submit" disabled={saving} className={btnPrimary} data-testid="whatsapp-save-btn">{saving ? "Salvataggio…" : "Salva impostazioni"}</button>
      </div>
    </form>
  );
}

function Simulator() {
  const [text, setText] = useState("Buongiorno, vorrei sapere gli orari per un pediatra");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/whatsapp/simulate", { text });
      setResult(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Panel data-testid="whatsapp-simulator">
      <div className="flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-[#5B2C91]" />
        <h3 className="font-heading text-xl font-semibold">Prova l'assistente</h3>
      </div>
      <p className="mt-1 text-sm text-[#6B6280]">Scrivi come farebbe un paziente: vedrai la risposta generata dalle disponibilità reali dei medici (nessun messaggio viene inviato).</p>
      <form onSubmit={run} className="mt-4 flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} className={inputCls} placeholder="es. avete un cardiologo disponibile?" data-testid="whatsapp-simulate-input" />
        <button type="submit" disabled={loading || !text.trim()} className={btnPrimary} data-testid="whatsapp-simulate-btn"><Send className="h-4 w-4" /> Prova</button>
      </form>
      {result && (
        <div className="mt-4 flex justify-end">
          <div className="max-w-[92%] rounded-3xl rounded-tr-md bg-[#DCF8C6] px-4 py-3 text-sm text-[#1F152E] shadow-sm whitespace-pre-wrap" data-testid="whatsapp-simulate-reply">
            {result.reply}
          </div>
        </div>
      )}
    </Panel>
  );
}

function MessagesLog() {
  const [messages, setMessages] = useState(null);
  const load = () => api.get("/whatsapp/messages", { params: { limit: 100 } }).then((r) => setMessages(r.data)).catch(() => setMessages([]));
  useEffect(() => {
    load();
  }, []);

  return (
    <Panel data-testid="whatsapp-messages">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-5 w-5 text-[#25D366]" />
          <h3 className="font-heading text-xl font-semibold">Ultimi messaggi</h3>
        </div>
        <button onClick={load} className={btnGhost} data-testid="whatsapp-messages-refresh"><RefreshCw className="h-3.5 w-3.5" /> Aggiorna</button>
      </div>
      {messages === null ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : messages.length === 0 ? (
        <p className="mt-4 text-sm text-[#8A809A]" data-testid="whatsapp-messages-empty">Nessun messaggio ricevuto finora. Quando il webhook sarà collegato, le conversazioni compariranno qui.</p>
      ) : (
        <ul className="mt-4 flex max-h-[520px] flex-col gap-2 overflow-y-auto pr-1">
          {messages.map((m) => (
            <li key={m.id} className={`flex ${m.direction === "out" ? "justify-end" : "justify-start"}`} data-testid={`whatsapp-message-${m.id}`}>
              <div className={`max-w-[85%] rounded-3xl px-4 py-2.5 text-sm shadow-sm ${m.direction === "out" ? "rounded-tr-md bg-[#DCF8C6]" : "rounded-tl-md bg-white ring-1 ring-[#5B2C91]/10"}`}>
                <p className="whitespace-pre-wrap">{m.text}</p>
                <p className="mt-1 text-[10px] text-[#8A809A]">
                  {m.direction === "in" ? `da +${m.phone}` : `a +${m.phone}`} · {fmtDateTime(m.created_at)}
                  {m.status === "failed" && <span className="ml-1 font-semibold text-red-600">· non inviato{m.error ? `: ${m.error}` : ""}</span>}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

const NOTIF_STATUS = {
  sent: { label: "Inviata", cls: "bg-emerald-100 text-emerald-800" },
  failed: { label: "Non inviata", cls: "bg-red-100 text-red-700" },
  skipped: { label: "Saltata", cls: "bg-slate-200 text-slate-600" },
};
const NOTIF_ROLE = { doctor: "Medico", nurse: "Infermiere", patient: "Paziente" };

function NotificationsLog() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/notifications", { params: { limit: 100 } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => {
    load();
  }, []);

  return (
    <Panel data-testid="notifications-log">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-[#5B2C91]" />
          <h3 className="font-heading text-xl font-semibold">Notifiche inviate</h3>
        </div>
        <button onClick={load} className={btnGhost} data-testid="notifications-refresh"><RefreshCw className="h-3.5 w-3.5" /> Aggiorna</button>
      </div>
      <p className="mt-1 text-sm text-[#6B6280]">Storico delle notifiche automatiche a medico, infermiere e paziente alla creazione o modifica di una visita.</p>
      {items === null ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : items.length === 0 ? (
        <p className="mt-4 text-sm text-[#8A809A]" data-testid="notifications-empty">Nessuna notifica ancora. Crea o modifica una visita per vederle qui.</p>
      ) : (
        <ul className="mt-4 flex max-h-[420px] flex-col gap-2 overflow-y-auto pr-1">
          {items.map((n) => {
            const st = NOTIF_STATUS[n.status] || { label: n.status, cls: "bg-slate-100 text-slate-700" };
            const Icon = n.channel === "email" ? Mail : MessageCircle;
            return (
              <li key={n.id} className="flex items-start gap-3 rounded-2xl bg-white px-3 py-2.5 ring-1 ring-[#5B2C91]/8" data-testid={`notification-${n.id}`}>
                <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${n.channel === "email" ? "text-[#5B2C91]" : "text-[#25D366]"}`} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-[#1F152E]">{n.headline} · {NOTIF_ROLE[n.role] || n.role}</p>
                  <p className="truncate text-xs text-[#6B6280]">{n.patient_name ? `${n.patient_name} — ` : ""}{n.recipient}</p>
                  {n.status === "failed" && n.error && <p className="mt-0.5 text-xs text-red-600">{n.error}</p>}
                  {n.status === "skipped" && n.error && <p className="mt-0.5 text-xs text-[#8A809A]">{n.error}</p>}
                  <p className="mt-0.5 text-[10px] text-[#8A809A]">{fmtDateTime(n.created_at)}</p>
                </div>
                <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${st.cls}`} data-testid={`notification-status-${n.id}`}>{st.label}</span>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

export default function AdminWhatsApp() {
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    api.get("/whatsapp/settings").then((r) => setSettings(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  return (
    <div data-testid="admin-whatsapp">
      <PageHeader eyebrow="WhatsApp" title="WhatsApp Cloud API" desc="Collega il numero aziendale tramite Meta WhatsApp Cloud API. Le richieste di informazioni ricevono automaticamente gli orari disponibili dei medici." />
      {settings === null ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
          <div className="xl:col-span-3">
            <Panel>
              <div className="mb-5 flex flex-wrap items-center gap-3" data-testid="whatsapp-status">
                <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${settings.configured ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`} data-testid="whatsapp-configured-badge">
                  <span className={`h-1.5 w-1.5 rounded-full ${settings.configured ? "bg-emerald-500" : "bg-amber-500"}`} />
                  {settings.configured ? "Cloud API configurata" : "Cloud API non configurata"}
                </span>
                <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${settings.active ? "bg-[#25D366]/15 text-emerald-800" : "bg-slate-200 text-slate-700"}`} data-testid="whatsapp-active-badge">
                  {settings.active ? "Canale attivo sul sito" : "Canale non attivo (coming soon)"}
                </span>
              </div>
              <SettingsForm settings={settings} onSaved={setSettings} />
            </Panel>
          </div>
          <div className="flex flex-col gap-6 xl:col-span-2">
            <Simulator />
            <NotificationsLog />
            <MessagesLog />
          </div>
        </div>
      )}
    </div>
  );
}
